import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import SearchableMultiSelect from "components/UI/Atoms/inputs/selects/searchableMultiselect";
import { ProfileManagementAPI } from "../components/Apis/userManagement";
import { fetchPermissions } from "../redux/permissions/permissionsSlice";

const UserManagement = () => {
    const [filters, setFilters] = useState({ search: "" });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [profiles, setProfiles] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [groups, setGroups] = useState([]);

    const { list: permissions, loading: permissionsLoading, error: permissionsError } = useSelector((state) => state.permissions);

    const fetchProfiles = () => {
        // Pass query params for pagination and search
        ProfileManagementAPI.getAll(
            page,
            pageSize,
            filters.search
        )
            .then((response) => {
                setProfiles(response.data.results);
                setTotalPages(Math.ceil(response.data.count / pageSize));
            })
            .catch((error) => {
                toast.error("Failed to fetch profiles");
                console.error(error);
            });
    };

    useEffect(() => {
        fetchProfiles();
    }, [page, pageSize, filters.search]); // re-fetch on changes

    const dispatch = useDispatch();
    useEffect(() => {
        if (!permissions) {
            console.log("Fetching permissions...");
            console.log("permissions:", permissions);
            dispatch(fetchPermissions());
        }
    }, [dispatch]);

    useEffect(() => {
        ProfileManagementAPI.getGroups()
            .then((response) => {
                const payload = response?.data;
                const nextGroups = Array.isArray(payload)
                    ? payload
                    : (payload?.results || []);
                setGroups(nextGroups);
            })
            .catch((error) => {
                toast.error("Failed to fetch groups");
                console.error(error);
            });
    }, []);

    return (
        <div className="main-container">
            <div className="pulse-section">
                <div className="pulse-header">
                    <h1>User List</h1>
                </div>

                <div className="pulse-body">
                    <div className="filters-panel">
                        <div className="search-row">
                            <div className="form-group w-100">
                                <label>Search</label>
                                <input
                                    type="text"
                                    className="input thin border"
                                    placeholder="Search users..."
                                    value={filters.search}
                                    onChange={(e) =>
                                        setFilters((prev) => ({ ...prev, search: e.target.value }))
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    <hr />

                    <table style={{ width: '100%' }} className="table">
                        <thead>
                            <tr>
                                <th>Username</th>
                                <th>Name</th>
                                <th>Email</th>

                                <th>Groups</th>
                                <th>Permissions</th>

                                <th>Act.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {profiles.map((profile) => (
                                <tr key={profile.id}>
                                    <td>{profile.user.username}</td>
                                    <td>{profile.user.first_name} {profile.user.last_name}</td>
                                    <td>{profile.user.email}</td>
                                    <td>
                                        <SearchableMultiSelect
                                            options={(groups || []).map((group) => ({
                                                value: group.id,
                                                label: group.name,
                                            }))}
                                            value={(profile.user.groups || []).map((group) => group.id)}
                                            onChange={(selectedValues) => {
                                                const nextValues = Array.isArray(selectedValues) ? selectedValues : [];
                                                const previousGroups = profile.user.groups || [];
                                                const selectedGroupObjects = nextValues
                                                    .map((groupId) => groups.find((group) => group.id === groupId))
                                                    .filter(Boolean);

                                                setProfiles((prev) =>
                                                    prev.map((p) =>
                                                        p.id === profile.id
                                                            ? {
                                                                ...p,
                                                                user: {
                                                                    ...p.user,
                                                                    groups: selectedGroupObjects,
                                                                },
                                                            }
                                                            : p
                                                    )
                                                );

                                                ProfileManagementAPI.updateGroups(profile.id, { groups: nextValues })
                                                    .then(() => toast.success("User groups updated"))
                                                    .catch((error) => {
                                                        setProfiles((prev) =>
                                                            prev.map((p) =>
                                                                p.id === profile.id
                                                                    ? {
                                                                        ...p,
                                                                        user: {
                                                                            ...p.user,
                                                                            groups: previousGroups,
                                                                        },
                                                                    }
                                                                    : p
                                                            )
                                                        );
                                                        toast.error(error.response?.data?.detail || "Failed to update user groups");
                                                        console.error(error);
                                                    });
                                            }}
                                            placeholder="Select groups..."
                                            maxMenuHeight={360}
                                            chipLimit={3}
                                        />
                                    </td>
                                    <td style={{ width: '600px' }}>
                                        <div>
                                            <SearchableMultiSelect
                                                options={
                                                    permissions
                                                        ? permissions.map((perm) => ({
                                                            value: perm.codename,
                                                            label: perm.name,
                                                        }))
                                                        : []
                                                }
                                                value={
                                                    profile.permissions
                                                        ? profile.permissions
                                                        : profile.user.user_permissions.map((perm) => perm.codename)
                                                }
                                                onChange={(selectedValues) => {
                                                    const nextValues = Array.isArray(selectedValues) ? selectedValues : [];
                                                    const selectedIds = nextValues.map((codename) => {
                                                        const perm = permissions.find((p) => p.codename === codename);
                                                        return perm?.id;
                                                    }).filter(Boolean);

                                                    setProfiles((prev) =>
                                                        prev.map((p) =>
                                                            p.id === profile.id ? { ...p, permissions: nextValues } : p
                                                        )
                                                    );

                                                    ProfileManagementAPI.updatePermissions(profile.id, { permissions: selectedIds })
                                                        .then(() => toast.success("User permissions updated"))
                                                        .catch((error) => {
                                                            toast.error(error.response?.data?.detail || "Failed to update user permissions");
                                                            console.error(error);
                                                        });
                                                }}
                                                placeholder="Select permissions..."
                                                maxMenuHeight={360}
                                                chipLimit={3}
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <button
                                            className="action-btn bg-danger"
                                            title="Deactivate User"
                                            onClick={
                                                () => ProfileManagementAPI.deactivateUser(profile.id)
                                                    .then(() => {
                                                        toast.success("User deactivated");
                                                        // Optionally refresh the user list or update state
                                                        setProfiles((prev) =>
                                                            prev.filter((p) => p.id !== profile.id)
                                                        );
                                                    })
                                                    .catch((error) => {
                                                        toast.error(error.response.data.detail ||  "Failed to deactivate user");
                                                        console.error(error);
                                                    })
                                            }
                                        >
                                            <i className="fa fa-user-slash" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <hr />
                    <div
                        style={{ display: "flex", justifyContent: "space-between" }}>
                        <div
                            style={{ flexDirection: "column" }}
                            className="pagination-buttons"
                        >
                            <label className="text-muted">Page Index:</label>
                            <div>
                                <button className="border-0 bg-none" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                                    <i className="fa fa-chevron-left" />
                                </button>
                                <span className="mx-3">{page} / {totalPages}</span>
                                <button className="border-0 bg-none" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                                    <i className="fa fa-chevron-right" />
                                </button>
                            </div>
                        </div>

                        <div
                            style={{ flexDirection: "column" }}
                            className="pagination-controls"
                        >
                            <label className="text-muted">Page Size:</label>
                            <SearchableMultiSelect
                                options={[5, 10, 25, 50, 100].map((size) => ({ value: size, label: String(size) }))}
                                value={pageSize}
                                onChange={(value) => {
                                    const next = Number(value) || 10;
                                    setPageSize(next);
                                }}
                                placeholder="Page size"
                                single
                                chipLimit={1}
                            />
                        </div>
                    </div>

                </div>
            </div>
            <Toaster position="bottom-right" reverseOrder={false} />
        </div>
    );
};

export default UserManagement;
